import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { TenantModule } from '@/store/api/endpoints/client/tenantsApi';

export { TenantModule };

/**
 * Information about a tenant
 */
export interface TenantInfo {
  tenantId: string;
  name: string;
  /** URL slug used for tenant-prefixed routes (e.g. "acme") */
  code?: string;
  type?: string;
  organizationId?: string;
  organizationName?: string;
}

/**
 * Payload for setting the active tenant.
 * Includes the authenticated user subject for session ownership/debugging.
 */
export interface SetActiveTenantPayload {
  tenant: TenantInfo;
  userSub?: string;
}

/**
 * Tenant state
 */
export interface TenantState {
  activeTenantId: string | null;
  activeTenantName: string | null;
  activeTenantOrgId: string | null;
  activeTenantOrgName: string | null;
  /** Brand tenant display name (for "Contract" field display) */
  brandTenantName: string | null;
  /** Brand tenant organization (for Logistic Provider prefill) */
  brandTenantOrgId: string | null;
  brandTenantOrgName: string | null;
  tenants: TenantInfo[];
  /** Modules the current tenant is allowed to use */
  modules: TenantModule[];
  /** The user sub associated with the resolved active tenant */
  storedUserSub: string | null;
  /** Explicit lifecycle for resolving signed tenant claims to memberships. */
  tenantResolutionStatus: 'idle' | 'loading' | 'ready' | 'error';
  tenantResolutionError: string | null;
}

const initialState: TenantState = {
  activeTenantId: null,
  activeTenantName: null,
  activeTenantOrgId: null,
  activeTenantOrgName: null,
  brandTenantName: null,
  brandTenantOrgId: null,
  brandTenantOrgName: null,
  tenants: [],
  modules: [],
  storedUserSub: null,
  tenantResolutionStatus: 'idle',
  tenantResolutionError: null,
};

export const tenantSlice = createSlice({
  name: 'tenant',
  initialState,
  reducers: {
    /**
     * Set the active tenant, optionally recording which user owns this selection.
     */
    setActiveTenant: (
      state,
      action: PayloadAction<SetActiveTenantPayload | null>,
    ) => {
      if (action.payload) {
        const { tenant, userSub } = action.payload;
        state.activeTenantId = tenant.tenantId;
        state.activeTenantName = tenant.name;
        state.activeTenantOrgId = tenant.organizationId ?? null;
        state.activeTenantOrgName = tenant.organizationName ?? null;
        if (userSub !== undefined) {
          state.storedUserSub = userSub;
        }
      } else {
        state.activeTenantId = null;
        state.activeTenantName = null;
        state.activeTenantOrgId = null;
        state.activeTenantOrgName = null;
      }
    },

    /**
     * Set the list of available tenants.
     */
    setTenants: (state, action: PayloadAction<TenantInfo[]>) => {
      state.tenants = action.payload;
      state.tenantResolutionStatus = 'ready';
      state.tenantResolutionError = null;
    },

    setTenantResolutionLoading: (state) => {
      state.tenantResolutionStatus = 'loading';
      state.tenantResolutionError = null;
    },

    setTenantResolutionError: (state, action: PayloadAction<string>) => {
      state.tenantResolutionStatus = 'error';
      state.tenantResolutionError = action.payload;
      state.tenants = [];
    },

    /**
     * Set the brand tenant (logistic provider).
     * Called after fetching from the dedicated eq-Type=brand query,
     * which is separate from the user's own tenant list.
     */
    setBrandTenant: (
      state,
      action: PayloadAction<{ name: string; organizationId?: string; organizationName?: string } | null>,
    ) => {
      if (action.payload) {
        state.brandTenantName = action.payload.name ?? null;
        state.brandTenantOrgId = action.payload.organizationId ?? null;
        state.brandTenantOrgName = action.payload.organizationName ?? null;
      } else {
        state.brandTenantName = null;
        state.brandTenantOrgId = null;
        state.brandTenantOrgName = null;
      }
    },

    /**
     * Set the modules the current tenant is allowed to use.
     * Fetched from GET /tenants after the active tenant is resolved.
     */
    setModules: (state, action: PayloadAction<TenantModule[]>) => {
      state.modules = action.payload;
    },

    /**
     * Clear tenant state (on logout)
     */
    clearTenant: (state) => {
      state.activeTenantId = null;
      state.activeTenantName = null;
      state.activeTenantOrgId = null;
      state.activeTenantOrgName = null;
      state.brandTenantName = null;
      state.brandTenantOrgId = null;
      state.brandTenantOrgName = null;
      state.tenants = [];
      state.modules = [];
      state.storedUserSub = null;
      state.tenantResolutionStatus = 'idle';
      state.tenantResolutionError = null;
    },
  },
});

export const {
  setActiveTenant,
  setTenants,
  setTenantResolutionLoading,
  setTenantResolutionError,
  setBrandTenant,
  setModules,
  clearTenant,
} = tenantSlice.actions;

// Selectors
export const selectTenantModules = (state: { tenant: TenantState }) => state.tenant.modules;
export const selectHasModule = (module: TenantModule) => (state: { tenant: TenantState }) =>
  state.tenant.modules.includes(module);

export default tenantSlice.reducer;
